import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { fadeUp, viewportOnce, stagger } from "../lib/motion";
import {
    PLANS,
    formatRand,
    STATUS_LABELS,
    PHASED_NOTE,
    FINE_PRINT,
    VAT_NOTE,
} from "../lib/plans";

const CHIP_TONES = {
    launch: "border-white/30 text-white/85",
    dev: "border-white/20 text-mist",
    roadmap: "border-white/20 border-dashed text-cad",
};

const StatusChip = ({ status }) =>
    status ? (
        <span
            className={`shrink-0 border px-2 py-0.5 font-mono text-[13px] leading-relaxed tracking-[0.18em] ${CHIP_TONES[status]}`}
        >
            {STATUS_LABELS[status]}
        </span>
    ) : null;

const PlanCards = () => (
    <section
        data-testid="plan-cards-section"
        className="relative bg-ink py-24 lg:py-32"
    >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="flex flex-wrap items-end justify-between gap-6"
            >
                <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
                    <span className="h-1.5 w-1.5 bg-accent" />
                    THE PLANS
                </p>
                <p
                    data-testid="plan-legend"
                    className="flex flex-wrap items-center gap-2.5 font-mono text-[13px] tracking-[0.18em] text-cad"
                >
                    <span className="border border-white/30 px-2 py-0.5 text-white/85">
                        {STATUS_LABELS.launch}
                    </span>
                    <span className="border border-white/20 px-2 py-0.5 text-mist">
                        {STATUS_LABELS.dev}
                    </span>
                    <span className="border border-dashed border-white/20 px-2 py-0.5">
                        {STATUS_LABELS.roadmap}
                    </span>
                    <span className="sr-only">
                        Availability legend: {STATUS_LABELS.launch}, available
                        when the plan opens; {STATUS_LABELS.dev}, in
                        development; {STATUS_LABELS.roadmap}, planned.
                    </span>
                </p>
            </motion.div>

            <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="mt-12 grid gap-6 lg:grid-cols-3"
            >
                {PLANS.map((plan) => (
                    <motion.div
                        key={plan.id}
                        variants={fadeUp}
                        data-testid={`plan-card-${plan.id}`}
                        className={`flex flex-col border bg-surface px-7 py-8 ${
                            plan.recommended
                                ? "order-1 border-accent lg:order-none"
                                : "order-2 border-white/12 lg:order-none"
                        }`}
                    >
                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                            <h3 className="font-mono text-[15px] tracking-[0.3em] text-white">
                                {plan.name}
                            </h3>
                            {plan.recommended ? (
                                <span
                                    data-testid="plan-recommended-chip"
                                    className="bg-accent px-2.5 py-1 font-mono text-[13px] tracking-[0.22em] text-white"
                                >
                                    RECOMMENDED
                                </span>
                            ) : null}
                        </div>
                        <p
                            data-testid={`plan-price-${plan.id}`}
                            className="mt-6 font-mono text-4xl font-semibold tracking-tight text-white"
                        >
                            {formatRand(plan.price)}
                        </p>
                        <p className="mt-2 font-mono text-[13px] tracking-[0.22em] text-cad">
                            {plan.priceNote}
                        </p>
                        <ul className="mt-8 flex-1 space-y-3.5 border-t border-white/10 pt-7">
                            {plan.features.map((f) => (
                                <li
                                    key={f.label}
                                    className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2"
                                >
                                    <span className="text-[15px] leading-relaxed text-mist">
                                        {f.label}
                                    </span>
                                    <StatusChip status={f.status} />
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/join-the-pilot"
                            data-testid={`plan-cta-${plan.id}`}
                            className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[15px] font-semibold transition-colors duration-300 ${
                                plan.recommended
                                    ? "bg-accent text-white hover:bg-[#ff7026]"
                                    : "border border-white/25 text-white hover:border-accent"
                            }`}
                        >
                            Join the pilot
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                        <p className="mt-3 text-center font-mono text-[13px] tracking-[0.2em] text-cad">
                            Paid plans open at launch.
                        </p>
                    </motion.div>
                ))}
            </motion.div>

            <p
                data-testid="vat-note"
                className="mt-8 font-mono text-[13px] tracking-[0.18em] text-cad"
            >
                {VAT_NOTE}
            </p>
            <p
                data-testid="phased-availability-note"
                className="mt-3 max-w-2xl text-[16px] leading-relaxed text-mist/85"
            >
                {PHASED_NOTE}
            </p>

            <div
                data-testid="plan-fine-print"
                className="mt-12 grid gap-6 border-t border-white/10 pt-10 md:grid-cols-3"
            >
                {FINE_PRINT.map((f) => (
                    <div key={f.k}>
                        <p className="font-mono text-[13px] tracking-[0.24em] text-white">
                            {f.k}
                        </p>
                        <p className="mt-3 text-[14px] leading-relaxed text-mist">
                            {f.v}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

export default PlanCards;
